import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageFrame } from '../../../media';
import VariableImage from '../../../media/VariableImage';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { accentOrPrimary } from '../../../puck/fields/createPuckColorField';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import { plainText } from '../../../utils/richTextHtml';
import { borderCssFromProps, BORDERLESS_BORDER } from '../../../../shared/boxes/borderStyles';
import { boxShadowCss } from '../../../../shared/boxes/shadowStyles';
import WithBlockSelection, { noSelection } from '../../../puck/WithBlockSelection';
import classes from './ImageComparisonBlock.module.css';

function ImageComparison({
  selectBlock, id, beforeSrc = '', afterSrc = '', beforeSrcVariable, afterSrcVariable,
  beforeSrcSrcset = '', afterSrcSrcset = '', beforeSrcForm, afterSrcForm,
  beforeAlt = '', afterAlt = '', beforeLabel = 'Avant', afterLabel = 'Après',
  beforeFocalPoint, afterFocalPoint, ratio = '16/9', accent = '',
  handleSize = '2.75rem', labelBackground = '#000000bb', border = BORDERLESS_BORDER, shadow = 'none', textStyles = {}, puck,
}) {
  const isEditing = !!puck?.isEditing;
  const [position, setPosition] = useState(50);
  const [touched, setTouched] = useState(false);
  const [failures, setFailures] = useState({});
  useEffect(() => setFailures({}), [beforeSrc, afterSrc]);
  const sides = [
    { side: 'before', src: beforeSrc, binding: beforeSrcVariable, srcSet: beforeSrcSrcset, form: beforeSrcForm, alt: beforeAlt, label: beforeLabel, focal: beforeFocalPoint },
    { side: 'after', src: afterSrc, binding: afterSrcVariable, srcSet: afterSrcSrcset, form: afterSrcForm, alt: afterAlt, label: afterLabel, focal: afterFocalPoint },
  ];
  const images = sides.filter((image) => image.src && failures[image.side] !== image.src
    && (!image.binding?.variableKey || image.binding.variableValue));
  const missingSides = sides.filter((side) => !images.includes(side));
  const comparing = images.length === 2;
  const sliderRef = useRef(null);
  const move = useCallback((next) => {
    setTouched(true);
    setPosition(Math.max(0, Math.min(100, next)));
  }, []);

  /* Écouteurs natifs : Puck démarre le glisser du bloc sur le `pointerdown` de
     son enveloppe, que seul un arrêt natif (pas celui de React) empêche. */
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return undefined;
    const moveToPointer = (event) => {
      const rect = slider.getBoundingClientRect();
      if (rect.width) move(Math.round((event.clientX - rect.left) / rect.width * 100));
    };
    const onDown = (event) => {
      event.stopPropagation();
      if (event.button !== 0) return;
      slider.setPointerCapture(event.pointerId);
      moveToPointer(event);
    };
    const onMove = (event) => { if (slider.hasPointerCapture(event.pointerId)) moveToPointer(event); };
    const onUp = (event) => { if (slider.hasPointerCapture(event.pointerId)) slider.releasePointerCapture(event.pointerId); };
    slider.addEventListener('pointerdown', onDown);
    slider.addEventListener('pointermove', onMove);
    slider.addEventListener('pointerup', onUp);
    return () => {
      slider.removeEventListener('pointerdown', onDown);
      slider.removeEventListener('pointermove', onMove);
      slider.removeEventListener('pointerup', onUp);
    };
  }, [comparing, move]);

  if (!images.length && !isEditing) return null;

  return (
    <div id={id} className={classes.root}>
      <div className={classes.frame} style={{ aspectRatio: ratio, ...borderCssFromProps(border), boxShadow: boxShadowCss(shadow), '--comparison-accent': accentOrPrimary(accent), '--comparison-handle': handleSize, '--comparison-label-background': labelBackground || 'transparent' }}>
        {images.map((image, index) => (
          <div key={image.side} className={classes.layer} style={comparing && index === 1 ? { clipPath: `inset(0 0 0 ${position}%)` } : undefined}>
            <VariableImage
              blockId={id}
              urlProp={`${image.side}Src`}
              src={image.src}
              binding={image.binding}
              variableBox={image.form?.showInForm}
              isEditing={isEditing}
              renderImage={(src) => <ImageFrame src={src} srcSet={image.binding?.variableKey ? '' : image.srcSet} alt={image.alt || plainText(image.label)} mediaFocalPoint={image.focal} asBackground isEditing={isEditing} onError={() => setFailures((previous) => ({ ...previous, [image.side]: image.src }))} />}
            />
          </div>
        ))}
        {comparing && (
          <>
            <div className={classes.separator} style={{ left: `${position}%` }} aria-hidden="true"><span><i className={classes.chevronLeft} /><i className={classes.chevronRight} /></span></div>
            <input
              ref={sliderRef}
              className={classes.slider}
              data-puck-overlay-portal={isEditing ? 'true' : undefined}
              type="range"
              min="0"
              max="100"
              value={position}
              aria-label="Comparer avant et après"
              aria-valuetext={`${position} % avant, ${100 - position} % après`}
              onChange={(event) => move(Number(event.target.value))}
              onClick={(event) => { event.stopPropagation(); selectBlock(); }}
              onKeyDown={(event) => {
                event.stopPropagation();
                const next = { ArrowLeft: position - 1, ArrowDown: position - 1, ArrowRight: position + 1, ArrowUp: position + 1, Home: 0, End: 100 }[event.key];
                if (next === undefined) return;
                event.preventDefault();
                move(next);
              }}
            />
            {!touched && <div className={classes.hint} aria-hidden="true">Faites glisser pour comparer</div>}
          </>
        )}
        {images.map((image) => (
          <div key={image.side} className={`${classes.label} ${image.side === 'after' ? classes.after : ''}`}>
            <PuckInlineRichText id={id} puck={puck} html={image.label} contentPath={`${image.side}Label`} reducedToolbar style={textRoleCss(textStyles, 'label')} placeholder={image.side === 'before' ? 'Avant' : 'Après'} />
          </div>
        ))}
        {isEditing && !comparing && (
          <div className={classes.placeholders}>
            {sides.map((image) => (
              <div key={image.side} className={missingSides.includes(image) ? classes.placeholder : undefined}>
                {missingSides.includes(image) && <>{`Image « ${plainText(image.label) || (image.side === 'before' ? 'Avant' : 'Après')} »`}<small>{image.binding?.variableKey ? 'Variable sans valeur pour le moment' : 'À choisir dans l’inspecteur'}</small></>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ImageComparisonBlock(props) {
  const BlockSelection = props.puck?.isEditing ? WithBlockSelection : noSelection;
  return <BlockSelection blockId={props.id}>{(selectBlock) => <ImageComparison {...props} selectBlock={selectBlock} />}</BlockSelection>;
}
